import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AdminStatsContent,
  AdminStatsEngagement,
  AdminStatsGrowth,
  AdminStatsOverview,
  AdminStatsPlatforms,
  AdminStatsRecent,
  getAdminStatsContent,
  getAdminStatsEngagement,
  getAdminStatsGrowth,
  getAdminStatsOverview,
  getAdminStatsPlatforms,
  getAdminStatsRecent,
} from '../api';
import { HorizontalBar, StatCard } from './StatsComponents';

type DateRange = 7 | 30 | 90 | 365;
type RecentTab = 'signups' | 'playlists' | 'feedback';

const PLATFORM_COLORS = ['#4b7bec', '#f59e0b', '#10b981', '#ef4444'];

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return formatDate(dateStr);
}

const chartTooltipStyle = {
  backgroundColor: '#1e222b',
  border: '1px solid #2a2f3a',
  borderRadius: '8px',
  color: '#e8eaf0',
  fontSize: '13px',
};

export default function AdminDashboard() {
  const [overview, setOverview] = useState<AdminStatsOverview | null>(null);
  const [growth, setGrowth] = useState<AdminStatsGrowth | null>(null);
  const [platforms, setPlatforms] = useState<AdminStatsPlatforms | null>(null);
  const [engagement, setEngagement] = useState<AdminStatsEngagement | null>(null);
  const [content, setContent] = useState<AdminStatsContent | null>(null);
  const [recent, setRecent] = useState<AdminStatsRecent | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(30);
  const [recentTab, setRecentTab] = useState<RecentTab>('signups');
  const [loading, setLoading] = useState(true);

  // Load all stats on mount
  useEffect(() => {
    Promise.all([
      getAdminStatsOverview(),
      getAdminStatsGrowth(dateRange),
      getAdminStatsPlatforms(),
      getAdminStatsEngagement(),
      getAdminStatsContent(),
      getAdminStatsRecent(),
    ])
      .then(([ov, gr, pl, en, co, re]) => {
        setOverview(ov);
        setGrowth(gr);
        setPlatforms(pl);
        setEngagement(en);
        setContent(co);
        setRecent(re);
      })
      .catch((err) => console.error('Failed to load admin stats:', err))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch growth data when date range changes
  useEffect(() => {
    if (!loading) {
      getAdminStatsGrowth(dateRange)
        .then(setGrowth)
        .catch((err) => console.error('Failed to load growth stats:', err));
    }
  }, [dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="admin-loading">Loading dashboard...</div>;
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-dashboard-header">
        <h2>Dashboard</h2>
        <div className="admin-date-range">
          {([7, 30, 90, 365] as DateRange[]).map((d) => (
            <button
              key={d}
              className={dateRange === d ? 'active' : ''}
              onClick={() => setDateRange(d)}
            >
              {d === 365 ? '1y' : `${d}d`}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Cards */}
      {overview && (
        <div className="admin-overview-grid">
          <StatCard label="Total Users" value={overview.total_users} />
          <StatCard label="Active (7d)" value={overview.active_7d} />
          <StatCard label="Active (30d)" value={overview.active_30d} />
          <StatCard label="Pro Users" value={overview.pro_users} />
          <StatCard label="Playlists" value={overview.total_playlists} />
          <StatCard label="Movies Watched" value={overview.total_watched} />
          <StatCard label="Friendships" value={overview.friendships} />
          <StatCard label="Feedback" value={overview.feedback} />
        </div>
      )}

      {/* Growth Charts */}
      {growth && (
        <div className="admin-charts-grid">
          <GrowthChart title="User Signups" data={growth.signups} color="#4b7bec" />
          <GrowthChart title="Playlists Created" data={growth.playlists} color="#10b981" />
          <GrowthChart title="Movies Watched" data={growth.watched} color="#f59e0b" />
        </div>
      )}

      {/* Platform Breakdown */}
      {platforms && (
        <div className="admin-two-col">
          <div className="admin-section">
            <h3>Platform Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={platforms.platforms.map((p) => ({
                    name: p.platform.toUpperCase(),
                    value: p.count,
                  }))}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                >
                  {platforms.platforms.map((_, i) => (
                    <Cell key={i} fill={PLATFORM_COLORS[i % PLATFORM_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={chartTooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="admin-section">
            <h3>Device Models</h3>
            <HorizontalBar
              items={platforms.device_models.map((d) => ({
                name: d.device_model,
                count: d.count,
              }))}
              maxCount={platforms.device_models[0]?.count || 1}
            />
          </div>
        </div>
      )}

      {/* Engagement */}
      {engagement && (
        <>
          <div className="admin-section">
            <h3>Engagement</h3>
            <div className="admin-stat-inline">
              <div className="admin-stat-inline-item">
                <span className="admin-stat-value">
                  {engagement.avg_playlists_per_user.toFixed(1)}
                </span>
                <span className="admin-stat-label">Avg playlists / user</span>
              </div>
              <div className="admin-stat-inline-item">
                <span className="admin-stat-value">
                  {engagement.avg_watched_per_user.toFixed(1)}
                </span>
                <span className="admin-stat-label">Avg watched / user</span>
              </div>
            </div>
          </div>
          <div className="admin-two-col">
            <div className="admin-section">
              <h3>Top Watchers</h3>
              <LeaderboardTable users={engagement.top_watchers} countLabel="Watched" />
            </div>
            <div className="admin-section">
              <h3>Top Playlist Creators</h3>
              <LeaderboardTable users={engagement.top_playlist_creators} countLabel="Playlists" />
            </div>
          </div>
        </>
      )}

      {/* Content */}
      {content && (
        <>
          <div className="admin-section">
            <h3>Most Watched Movies</h3>
            <div className="admin-movie-grid">
              {content.most_watched.map((m) => (
                <Link
                  key={m.tmdb_id}
                  to={`/movies/${m.tmdb_id}`}
                  className="admin-movie-item"
                  style={{ textDecoration: 'none' }}
                >
                  {m.poster_url ? (
                    <img
                      src={`https://image.tmdb.org/t/p/w185${m.poster_url}`}
                      alt={m.title || ''}
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        aspectRatio: '2/3',
                        background: '#1e222b',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6b7280',
                        fontSize: 12,
                      }}
                    >
                      No poster
                    </div>
                  )}
                  <span className="admin-movie-title">
                    {m.title || 'Unknown'} {m.year ? `(${m.year})` : ''}
                  </span>
                  <span className="admin-movie-count">{m.watch_count} watches</span>
                </Link>
              ))}
            </div>
          </div>
          <div className="admin-two-col">
            <div className="admin-section">
              <h3>Playlist Types</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'AI Generated', value: content.playlist_types.ai_generated },
                      { name: 'Manual', value: content.playlist_types.manual },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  >
                    <Cell fill="#4b7bec" />
                    <Cell fill="#f59e0b" />
                  </Pie>
                  <Tooltip contentStyle={chartTooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="admin-section">
              <h3>Playlist Statuses</h3>
              <HorizontalBar
                items={content.playlist_statuses.map((s) => ({
                  name: s.status,
                  count: s.count,
                }))}
                maxCount={Math.max(...content.playlist_statuses.map((s) => s.count), 1)}
              />
            </div>
          </div>
        </>
      )}

      {/* Recent Activity */}
      {recent && (
        <div className="admin-section">
          <h3>Recent Activity</h3>
          <div className="admin-tabs">
            {(['signups', 'playlists', 'feedback'] as RecentTab[]).map((tab) => (
              <button
                key={tab}
                className={recentTab === tab ? 'active' : ''}
                onClick={() => setRecentTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {recentTab === 'signups' && (
            <div className="admin-recent-list">
              {recent.signups.map((u) => (
                <Link
                  key={u.id}
                  to={`/admin/users/${u.id}`}
                  className="admin-recent-item"
                  style={{ textDecoration: 'none' }}
                >
                  {u.picture ? (
                    <img src={u.picture} alt="" className="admin-recent-avatar" />
                  ) : (
                    <div className="admin-recent-avatar" />
                  )}
                  <div className="admin-recent-info">
                    <div className="admin-recent-name">{u.name || u.email}</div>
                    <div className="admin-recent-detail">
                      {u.username ? `@${u.username}` : u.email}
                    </div>
                  </div>
                  <span className="admin-recent-time">{timeAgo(u.created_at)}</span>
                </Link>
              ))}
            </div>
          )}

          {recentTab === 'playlists' && (
            <div className="admin-recent-list">
              {recent.playlists.map((p) => (
                <Link
                  key={p.id}
                  to={`/playlists/${p.id}`}
                  className="admin-recent-item"
                  style={{ textDecoration: 'none' }}
                >
                  <div className="admin-recent-info">
                    <div className="admin-recent-name">{p.name}</div>
                    <div className="admin-recent-detail">
                      by {p.user_name || p.user_email}
                      {p.prompt ? ' — AI' : ''}
                    </div>
                  </div>
                  <span className={`admin-status-badge ${p.status}`}>{p.status}</span>
                  <span className="admin-recent-time">{timeAgo(p.created_at)}</span>
                </Link>
              ))}
            </div>
          )}

          {recentTab === 'feedback' && (
            <div className="admin-recent-list">
              {recent.feedback.map((f) => (
                <div key={f.id} className="admin-recent-item">
                  <div className="admin-recent-info">
                    <div className="admin-recent-name">
                      <span
                        className={`admin-status-badge ${f.category === 'bug' ? 'failed' : 'processing'}`}
                      >
                        {f.category}
                      </span>{' '}
                      {f.user_name || f.user_email}
                    </div>
                    <div className="admin-recent-detail">{f.message}</div>
                  </div>
                  <span className="admin-recent-time">{timeAgo(f.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Admin Tools */}
      <div className="admin-section">
        <h3>Admin Tools</h3>
        <div className="admin-tools-grid">
          <Link to="/admin/users" className="admin-tool-link">
            Users &amp; Backfill
          </Link>
          <Link to="/admin/feedback" className="admin-tool-link">
            Feedback
          </Link>
          <Link to="/admin/movies" className="admin-tool-link">
            Movie Search
          </Link>
        </div>
      </div>
    </div>
  );
}

function GrowthChart({
  title,
  data,
  color,
}: {
  title: string;
  data: Array<{ date: string; count: number }>;
  color: string;
}) {
  const chartData = data.map((d) => ({ date: formatDate(d.date), count: d.count }));

  return (
    <div className="admin-chart-card">
      <h4>{title}</h4>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2f3a" />
          <XAxis
            dataKey="date"
            stroke="#6b7280"
            fontSize={11}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis stroke="#6b7280" fontSize={11} tickLine={false} allowDecimals={false} />
          <Tooltip contentStyle={chartTooltipStyle} />
          <Line type="monotone" dataKey="count" stroke={color} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function LeaderboardTable({
  users,
  countLabel,
}: {
  users: Array<{
    id: number;
    name: string | null;
    email: string;
    username: string | null;
    count: number;
  }>;
  countLabel: string;
}) {
  return (
    <table className="admin-leaderboard-table">
      <thead>
        <tr>
          <th>#</th>
          <th>User</th>
          <th>{countLabel}</th>
        </tr>
      </thead>
      <tbody>
        {users.map((u, i) => (
          <tr key={u.id}>
            <td>{i + 1}</td>
            <td>
              <Link to={`/admin/users/${u.id}`}>{u.name || u.username || u.email}</Link>
            </td>
            <td>{u.count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
